import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import './PaymentResultPage.css';

type ResultStatus = 'loading' | 'approved' | 'pending' | 'declined' | 'error' | 'not_found';

interface PaymentInfo {
  reference: string;
  status: string;
  appointmentDate?: string;
  appointmentTime?: string;
  serviceName?: string;
  clientName?: string;
  amount?: number;
}

function buildGoogleCalendarUrl(params: {
  serviceName: string;
  date: string;
  time: string;
}): string {
  const { serviceName, date, time } = params;
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  const startUTC = new Date(Date.UTC(year, month - 1, day, hour + 5, minute, 0));
  const endUTC = new Date(startUTC.getTime() + 40 * 60 * 1000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const text = encodeURIComponent(`${serviceName} – Barber Studio Luisfer`);
  const dates = `${fmt(startUTC)}/${fmt(endUTC)}`;
  const details = encodeURIComponent(
    `Servicio: ${serviceName}\nBarbero: Luisfer\nDuración: 40 minutos\n\n⚠️ Por favor llega 10 minutos antes de tu cita.`
  );
  const location = encodeURIComponent('Calle 27b # 11-47, Barranquilla, Colombia');
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${dates}&details=${details}&location=${location}&sf=true&output=xml`;
}

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ResultStatus>('loading');
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Wompi returns these query params on redirect:
    // id (transaction id), reference, payment_method_type, amount_in_cents, currency, status
    const wompiRef = searchParams.get('reference');
    const wompiStatus = searchParams.get('status');

    if (!wompiRef) {
      setStatus('not_found');
      return;
    }

    // Fetch payment + appointment details from Supabase
    const fetchDetails = async () => {
      const { data: payment, error } = await supabase
        .from('payments')
        .select('id, reference, status, amount, appointment_id')
        .eq('reference', wompiRef)
        .maybeSingle();

      if (error || !payment) {
        setStatus('not_found');
        return;
      }

      let appointmentDate: string | undefined;
      let appointmentTime: string | undefined;
      let serviceName: string | undefined;
      let clientName: string | undefined;

      if (payment.appointment_id) {
        const { data: appt } = await supabase
          .from('appointments')
          .select('appointment_date, start_time, client_name, service_id')
          .eq('id', payment.appointment_id)
          .maybeSingle();

        if (appt) {
          appointmentDate = appt.appointment_date as string;
          appointmentTime = (appt.start_time as string).slice(0, 5);
          clientName = appt.client_name as string;

          if (appt.service_id) {
            const { data: svc } = await supabase
              .from('services')
              .select('name')
              .eq('id', appt.service_id)
              .maybeSingle();
            if (svc) serviceName = svc.name as string;
          }
        }
      }

      setPaymentInfo({
        reference: payment.reference as string,
        status: payment.status as string,
        appointmentDate,
        appointmentTime,
        serviceName,
        clientName,
        amount: payment.amount as number,
      });

      // Determine display status — prefer DB status (updated by webhook),
      // fall back to Wompi query param if webhook hasn't fired yet.
      // Business rule: failed payments → no_show (advance kept as income, slot freed).
      const dbStatus = payment.status as string;
      if (dbStatus === 'paid') {
        setStatus('approved');
      } else if (dbStatus === 'failed') {
        // Payment failed → appointment is no_show, slot freed
        setStatus('declined');
      } else {
        // Webhook may not have fired yet — use Wompi's query param
        const ws = (wompiStatus ?? '').toUpperCase();
        if (ws === 'APPROVED') setStatus('approved');
        else if (ws === 'DECLINED') setStatus('declined');
        else if (ws === 'ERROR') setStatus('error');
        else setStatus('pending');
      }
    };

    fetchDetails();
  }, [searchParams]);

  return (
    <div className="payment-result-page">
      {/* Top bar */}
      <header className="payment-result-topbar">
        <Link to="/" className="payment-result-topbar-back">
          <div className="payment-result-topbar-back-icon">
            <i className="ri-arrow-left-line" />
          </div>
          <span className="payment-result-topbar-back-label">Volver al inicio</span>
        </Link>
        <div className="payment-result-topbar-brand">
          <span className="payment-result-topbar-brand-text">
            Barber Studio <span className="payment-result-topbar-brand-accent">Luisfer</span>
          </span>
        </div>
        <div className="w-28 hidden md:block" />
      </header>

      <main className="payment-result-main">
        {status === 'loading' && <LoadingState />}
        {status === 'approved' && <ApprovedState info={paymentInfo} />}
        {status === 'pending' && <PendingState info={paymentInfo} />}
        {(status === 'declined' || status === 'error') && <DeclinedState info={paymentInfo} />}
        {status === 'not_found' && <NotFoundState />}
      </main>
    </div>
  );
}

// ─── Sub-states ───────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="payment-result-center">
      <div className="payment-result-spinner">
        <i className="ri-loader-4-line animate-spin" />
      </div>
      <p className="payment-result-loading-text">Verificando tu pago...</p>
    </div>
  );
}

function ApprovedState({ info }: { info: PaymentInfo | null }) {
  const calUrl =
    info?.appointmentDate && info?.appointmentTime && info?.serviceName
      ? buildGoogleCalendarUrl({
          serviceName: info.serviceName,
          date: info.appointmentDate,
          time: info.appointmentTime,
        })
      : null;

  const formattedDate = info?.appointmentDate
    ? new Date(info.appointmentDate + 'T00:00:00').toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div className="payment-result-card">
      <div className="payment-result-icon payment-result-icon--success">
        <i className="ri-checkbox-circle-fill" />
      </div>
      <p className="payment-result-eyebrow">◆ Pago confirmado ◆</p>
      <h1 className="payment-result-title">¡Cita reservada!</h1>
      <p className="payment-result-message">
        {info?.clientName ? `¡Gracias, ${info.clientName.split(' ')[0]}! ` : ''}
        Tu pago fue procesado exitosamente y tu cita ha sido confirmada.
      </p>

      {info && (
        <div className="payment-result-details">
          {info.serviceName && (
            <div className="payment-result-detail-row">
              <div className="payment-result-detail-icon"><i className="ri-scissors-line" /></div>
              <div>
                <p className="payment-result-detail-label">Servicio</p>
                <p className="payment-result-detail-value">{info.serviceName}</p>
              </div>
            </div>
          )}
          {formattedDate && (
            <div className="payment-result-detail-row">
              <div className="payment-result-detail-icon"><i className="ri-calendar-check-line" /></div>
              <div>
                <p className="payment-result-detail-label">Fecha</p>
                <p className="payment-result-detail-value">{formattedDate}</p>
              </div>
            </div>
          )}
          {info.appointmentTime && (
            <div className="payment-result-detail-row">
              <div className="payment-result-detail-icon"><i className="ri-time-line" /></div>
              <div>
                <p className="payment-result-detail-label">Hora</p>
                <p className="payment-result-detail-value">{info.appointmentTime}</p>
              </div>
            </div>
          )}
          {info.amount !== undefined && (
            <div className="payment-result-detail-row">
              <div className="payment-result-detail-icon"><i className="ri-money-dollar-circle-line" /></div>
              <div>
                <p className="payment-result-detail-label">Total pagado</p>
                <p className="payment-result-detail-value">${info.amount.toLocaleString('es-CO')} COP</p>
              </div>
            </div>
          )}
          <div className="payment-result-detail-row">
            <div className="payment-result-detail-icon"><i className="ri-file-list-3-line" /></div>
            <div>
              <p className="payment-result-detail-label">Referencia</p>
              <p className="payment-result-detail-value payment-result-ref">{info.reference}</p>
            </div>
          </div>
        </div>
      )}

      <div className="payment-result-reminder">
        <div className="payment-result-reminder-icon"><i className="ri-alarm-warning-line" /></div>
        <p className="payment-result-reminder-text">
          Recuerda llegar <strong>10 minutos antes</strong> de tu cita para una mejor atención.
        </p>
      </div>

      <div className="payment-result-actions">
        {calUrl && (
          <a
            href={calUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="payment-result-btn-secondary"
          >
            <i className="ri-calendar-event-line" /> Agregar a Google Calendar
          </a>
        )}
        <Link to="/" className="payment-result-btn-primary">
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

function PendingState({ info }: { info: PaymentInfo | null }) {
  return (
    <div className="payment-result-card">
      <div className="payment-result-icon payment-result-icon--pending">
        <i className="ri-time-line" />
      </div>
      <p className="payment-result-eyebrow">◆ En proceso ◆</p>
      <h1 className="payment-result-title">Pago en verificación</h1>
      <p className="payment-result-message">
        Tu pago está siendo procesado. Esto puede tomar unos minutos. Recibirás una confirmación una vez que se complete.
      </p>
      {info?.reference && (
        <p className="payment-result-ref-inline">
          Referencia: <span>{info.reference}</span>
        </p>
      )}
      <div className="payment-result-actions">
        <Link to="/reservar" className="payment-result-btn-secondary">
          <i className="ri-calendar-line" /> Nueva reserva
        </Link>
        <Link to="/" className="payment-result-btn-primary">
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

function DeclinedState({ info }: { info: PaymentInfo | null }) {
  return (
    <div className="payment-result-card">
      <div className="payment-result-icon payment-result-icon--error">
        <i className="ri-close-circle-fill" />
      </div>
      <p className="payment-result-eyebrow">◆ Pago rechazado ◆</p>
      <h1 className="payment-result-title">No se pudo procesar</h1>
      <p className="payment-result-message">
        Tu pago fue rechazado o hubo un error en la transacción. El horario ha quedado disponible nuevamente. Puedes intentar una nueva reserva con otro método de pago.
      </p>
      {info?.reference && (
        <p className="payment-result-ref-inline">
          Referencia: <span>{info.reference}</span>
        </p>
      )}
      <div className="payment-result-actions">
        <Link to="/reservar" className="payment-result-btn-primary">
          <i className="ri-refresh-line" /> Intentar de nuevo
        </Link>
        <Link to="/" className="payment-result-btn-secondary">
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="payment-result-card">
      <div className="payment-result-icon payment-result-icon--pending">
        <i className="ri-question-line" />
      </div>
      <h1 className="payment-result-title">Pago no encontrado</h1>
      <p className="payment-result-message">
        No pudimos encontrar información sobre este pago. Si crees que es un error, contáctanos.
      </p>
      <div className="payment-result-actions">
        <Link to="/reservar" className="payment-result-btn-secondary">
          <i className="ri-calendar-line" /> Nueva reserva
        </Link>
        <Link to="/" className="payment-result-btn-primary">
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}
